import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bug, AlertTriangle, Clock, CheckCircle, XCircle, Filter, Search, ChevronDown, ExternalLink, Trash2, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorityColors = {
  low: 'bg-slate-500',
  medium: 'bg-blue-500',
  high: 'bg-orange-500',
  critical: 'bg-red-500'
};

const statusColors = {
  open: 'bg-yellow-500',
  in_progress: 'bg-blue-500',
  resolved: 'bg-green-500',
  closed: 'bg-slate-500'
};

const statusIcons = {
  open: AlertTriangle,
  in_progress: Clock,
  resolved: CheckCircle,
  closed: XCircle
};

const BugReports = () => {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ open_count: 0, in_progress_count: 0, critical_count: 0 });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBug, setSelectedBug] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadBugs();
    loadStats();
  }, [filterStatus, filterPriority]);

  const loadBugs = async () => {
    try {
      let url = `${API}/bugs`;
      const params = [];
      if (filterStatus !== 'all') params.push(`status=${filterStatus}`);
      if (filterPriority !== 'all') params.push(`priority=${filterPriority}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setBugs(response.data);
    } catch (error) {
      toast.error('Failed to load bug reports');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/bugs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleEditBug = (bug) => {
    setSelectedBug(bug);
    setEditForm({
      status: bug.status,
      priority: bug.priority,
      admin_notes: bug.admin_notes || ''
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/bugs/${selectedBug.id}`, editForm);
      toast.success('Bug report updated');
      setEditModalOpen(false);
      loadBugs();
      loadStats();
    } catch (error) {
      toast.error('Failed to update bug report');
    }
  };

  const handleDeleteBug = async (bugId) => {
    if (!window.confirm('Are you sure you want to delete this bug report?')) return;
    
    try {
      await axios.delete(`${API}/bugs/${bugId}`);
      toast.success('Bug report deleted');
      loadBugs();
      loadStats();
    } catch (error) {
      toast.error('Failed to delete bug report');
    }
  };

  const filteredBugs = bugs.filter(bug => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return bug.title.toLowerCase().includes(search) || 
             bug.description.toLowerCase().includes(search) ||
             bug.reported_by_name.toLowerCase().includes(search);
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="bug-reports-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Bug className="h-6 w-6 text-primary" />
            Bug Reports
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track and manage reported bugs and issues
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.open_count}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Open</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Clock className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.in_progress_count}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">In Progress</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/10 rounded-lg">
              <Bug className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.critical_count}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Critical/High</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.total_active}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Active</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center bg-card border border-border rounded-lg p-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search bugs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="bug-search-input"
            />
          </div>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[150px]" data-testid="priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bug List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Loading bugs...</div>
        ) : filteredBugs.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Bug className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No bug reports found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredBugs.map((bug) => {
              const StatusIcon = statusIcons[bug.status] || AlertTriangle;
              return (
                <div
                  key={bug.id}
                  className="p-4 hover:bg-accent/50 transition-colors"
                  data-testid={`bug-item-${bug.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${statusColors[bug.status]}/10`}>
                      <StatusIcon className={`h-5 w-5 ${statusColors[bug.status].replace('bg-', 'text-')}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">{bug.title}</h3>
                        <Badge className={`${priorityColors[bug.priority]} text-white text-xs`}>
                          {bug.priority}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {bug.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {bug.description}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span>Reported by: <strong className="text-foreground">{bug.reported_by_name}</strong></span>
                        <span>{formatDate(bug.created_at)}</span>
                        {bug.page_url && (
                          <a 
                            href={bug.page_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View Page
                          </a>
                        )}
                      </div>
                      {bug.admin_notes && (
                        <div className="mt-2 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
                          <strong>Admin Notes:</strong> {bug.admin_notes}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditBug(bug)}
                        data-testid={`edit-bug-${bug.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteBug(bug.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-bug-${bug.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Bug Report</DialogTitle>
          </DialogHeader>
          {selectedBug && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Bug Title</p>
                <p className="text-sm text-muted-foreground">{selectedBug.title}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Status</label>
                <Select 
                  value={editForm.status} 
                  onValueChange={(val) => setEditForm({...editForm, status: val})}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Priority</label>
                <Select 
                  value={editForm.priority} 
                  onValueChange={(val) => setEditForm({...editForm, priority: val})}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Admin Notes</label>
                <Textarea
                  value={editForm.admin_notes}
                  onChange={(e) => setEditForm({...editForm, admin_notes: e.target.value})}
                  placeholder="Add notes about this bug..."
                  className="mt-1"
                  rows={3}
                  data-testid="edit-admin-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} data-testid="save-bug-edit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BugReports;
